<script lang="ts">
    import Fa from 'svelte-fa'
    import { faPenNib, faBell, faUserCircle, faUser } from '@fortawesome/free-solid-svg-icons'
    import type { Card } from '../../modules/pipelineApi/pipeline/cards/card'
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'
    import dayjs from 'dayjs'
    import utc from 'dayjs/plugin/utc'
    import _isToday from 'dayjs/plugin/isToday'
    import { Spinner } from 'sveltestrap'
    dayjs.extend(utc)
    dayjs.extend(_isToday)
    const dispatch = createEventDispatcher()

    export let card: Card
    export let index = 0

    function editCard() {
        dispatch('edit', card)
    }

    function openPerson() {
        if (card.personId) {
            window.person('show', card.personId, 4)
        }
    }

    let friendlyCreatedAt = null
    let isNew = null
    let refreshIsNew
    function evaluateCreatedAt() {
        const diff = dayjs(new Date()).diff(card.createdAt, 'second')
        if (diff <= 24 * 60 * 60) {
            isNew = true
            friendlyCreatedAt = 'Novo: criado ' + window.api.utils.utils.dateFriendly(card?.createdAt)
            if (refreshIsNew) clearTimeout(refreshIsNew)
            if (diff <= 120) {
                // 2 minutos
                refreshIsNew = setTimeout(evaluateCreatedAt, 5000)
            } else if (diff <= 60 * 60) {
                // 1 hr
                refreshIsNew = setTimeout(evaluateCreatedAt, 60000)
            } else if (diff <= 60 * 60 * 48) {
                // 48 hrs
                refreshIsNew = setTimeout(evaluateCreatedAt, 60000)
            }
            return
        }
        isNew = false
        if (diff <= 24 * 60 * 60 * 30) {
            friendlyCreatedAt = 'Criado ' + window.api.utils.utils.dateFriendly(card?.createdAt)
            return
        }
        friendlyCreatedAt = 'Criado em ' + dayjs(card?.createdAt).format('DD/MM/YYYY')
    }

    let isPast = false
    let isFuture = false
    let isToday = false

    function friendlyScheduler(date) {
        if (!date) return ''
        const daysDiff = dayjs(card.scheduledAt).utc().diff(new Date(), 'day')
        if (dayjs(card.scheduledAt).utc().isToday()) {
            isToday = true
            return 'Lembrete para hoje'
        }
        if (daysDiff < 0) {
            isPast = true
            if (daysDiff === -1) return '1 dia atrasado'
            return -daysDiff + ' dias atrasado'
        }
        if (daysDiff > 0) isFuture = true
        if (daysDiff <= 1 && !dayjs(card.scheduledAt).utc().isToday()) {
            return 'Lembrete para amanhã'
        }
        return 'Lembrete para ' + dayjs(card.scheduledAt).utc().format('DD/MM/YY')
    }

    let loaded = false
    onMount(() => {
        evaluateCreatedAt()
        loaded = true
    })

    onDestroy(() => {
        if (refreshIsNew) clearTimeout(refreshIsNew)
    })
</script>

{#if loaded}
    <div class="pipeline_card" on:click={editCard}>
        <div class="card_head">
            {#if card.personId}
                <div class="pic">
                    {#if index < 20}
                        <img
                            src="https://zandu-customers-files.s3.amazonaws.com/{window.store.token.companyId}/persons-pics/{card?.personId}.jpeg"
                            title=""
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    {/if}
                </div>
            {/if}
            <div title={card.title} class="name">
                <div class="name_content">
                    {card.title}
                </div>
                {#if card.personId}
                    <button class="btn btn-sm" on:click|stopPropagation={openPerson}><Fa icon={faUser} /></button>
                {/if}
            </div>
        </div>
        {#if card.text || friendlyCreatedAt}
            <div class="body">
                <div>
                    {@html card.text.replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('\n', '<br />') || ''}
                </div>
                {#if friendlyCreatedAt}
                    <div class="badge" class:card_new={isNew}>
                        {friendlyCreatedAt}
                    </div>
                {/if}
            </div>
        {/if}
        <div class="footer">
            {#if card.scheduledAt}
                <div class="schedule mb-1" class:isPast class:isFuture class:isToday>
                    {#if isToday}
                        <Fa icon={faBell} />
                    {/if}
                    {friendlyScheduler(card.scheduledAt)}
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    .pipeline_card {
        background: #3f37ac;
        color: white;
        min-height: 40px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border-radius: 3px;
        user-select: none;
        cursor: pointer;
        border: 1px solid #32886640;
        box-shadow: 0px 3px 7px #33333350;
        margin: 2px 0px 10px 0px;
    }
    .card_new {
        background: #217755;
    }
    .badge {
        padding-top: 2px;
        background: #99999950;
    }
    .card_head {
        background: #00000015;
        font-size: 14px;
        font-weight: 300;
        display: flex;
        align-items: center;
        min-height: 50px;
    }
    .card_head .pic {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 50px;
        width: 50px;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 50%;
        margin: 4px;
        background: #66666630;
    }
    .card_head .pic img {
        height: 100%;
        max-width: 50px;
    }
    .card_head .name {
        display: flex;
        flex-grow: 1;
    }
    .card_head .name .btn {
        margin-right: 4px;
    }
    .card_head .name .btn:hover {
        background: #99999935;
    }
    .card_head .name_content {
        flex-grow: 1;
        padding: 4px;
        overflow: hidden;
    }
    .body {
        flex: 1 1 auto;
        padding: 4px 4px 4px 4px;
        font-size: 13px;
        line-height: 1.3;
        opacity: 0.8;
    }
    .footer {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
    }
    .footer .schedule {
        font-size: 13px;
        padding: 3px;
        margin-left: 4px;
        border-radius: 4px;
        border: 1px solid #99999920;
        background-color: #99999922;
        display: flex;
        align-items: center;
    }
    .footer .schedule > :global(svg) {
        margin-right: 4px;
    }
    .isPast {
        color: rgb(214, 79, 122) !important;
        border: 1px solid rgba(214, 79, 122, 0.2) !important;
        background-color: rgba(214, 79, 122, 0.2) !important;
    }
    .isFuture {
        opacity: 0.4;
    }
    .isToday {
        color: rgb(46, 204, 113) !important;
        border: 1px solid rgba(46, 204, 113, 0.2) !important;
        background-color: rgba(46, 204, 113, 0.2) !important;
    }
</style>
